"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils/general-utils";
import { Spinner } from "@/components/ui/primitives/spinner";
import { Plus } from "lucide-react";
import React from "react";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-lg text-sm font-semibold whitespace-nowrap transition-colors duration-200 outline-none select-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-white shadow-sm hover:bg-brand-900 active:bg-brand-950",
        secondary:
          "bg-gray-100 text-gray-900 shadow-sm border border-gray-200 hover:bg-gray-200/80",
        outline:
          "border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 hover:text-gray-900 active:bg-gray-100",
        ghost:
          "text-gray-700 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200",
        destructive:
          "bg-red-600 text-white shadow-sm hover:bg-red-700 active:bg-red-800 focus-visible:ring-red-500/40",
        action:
          "border border-primary text-primary bg-white shadow-sm hover:bg-primary hover:text-white active:bg-brand-700 active:text-white [&_svg]:text-primary hover:[&_svg]:text-white",
        "action-danger":
          "border border-gray-300 text-gray-700 bg-white shadow-sm hover:text-status-error-text hover:border-red-600 [&_svg]:text-gray-700 hover:[&_svg]:text-status-error-text active:text-red-700 active:border-red-700",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-3.5 py-2",
        sm: "h-8 px-3 text-xs",
        lg: "h-10 px-5",
        icon: "size-9",
        "icon-sm":
          "size-7 rounded-[min(var(--radius-md),12px)] [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonColor =
  | "zinc"
  | "dark/zinc"
  | "light"
  | "dark/white"
  | "dark"
  | "white"
  | "cyan"
  | "red"
  | "orange"
  | "amber"
  | "yellow"
  | "lime"
  | "green"
  | "emerald"
  | "teal"
  | "sky"
  | "blue"
  | "violet"
  | "purple"
  | "fuchsia"
  | "pink"
  | "rose";

type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>["variant"]
>;

interface ButtonProps {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  form?: string;
  loading?: boolean;
  loadingText?: string;
  /** @deprecated Use `variant` instead. Kept for backward compatibility. */
  color?: ButtonColor;
  /** @deprecated Use `variant="outline"` instead. Kept for backward compatibility. */
  outline?: boolean;
  /** @deprecated Use `variant="ghost"` instead. Kept for backward compatibility. */
  plain?: boolean;
  variant?: ButtonVariant;
  size?: NonNullable<VariantProps<typeof buttonVariants>["size"]>;
  style?: React.CSSProperties;
  title?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  "aria-current"?:
    boolean | "true" | "false" | "page" | "step" | "location" | "date" | "time";
}

function resolveVariant({
  variant,
  color,
  outline,
  plain,
}: Pick<
  ButtonProps,
  "variant" | "color" | "outline" | "plain"
>): ButtonVariant {
  if (variant) return variant;
  if (outline) return "outline";
  if (plain) return "ghost";
  if (color === "red") return "destructive";
  if (color === "zinc" || color === "dark/zinc" || color === "dark")
    return "secondary";
  return "default";
}

export const Button = React.memo(
  React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
      {
        children,
        icon,
        onClick,
        className = "",
        disabled = false,
        type = "button",
        loading = false,
        loadingText,
        color,
        outline,
        plain,
        variant: variantProp,
        size,
        ...props
      },
      ref,
    ) => {
      const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        try {
          onClick?.(event);
        } catch (error) {
          console.error("Button click error:", error);
        }
      };

      const isDisabled = disabled || loading;
      const resolvedVariant = resolveVariant({
        variant: variantProp,
        color,
        outline,
        plain,
      });

      return (
        <button
          ref={ref}
          type={type}
          onClick={handleClick}
          disabled={isDisabled}
          aria-disabled={isDisabled}
          className={cn(
            buttonVariants({ variant: resolvedVariant, size }),
            className,
          )}
          {...props}
        >
          {loading ? (
            <>
              <span className="invisible flex items-center gap-2">
                {icon && (
                  <span className="flex items-center" aria-hidden="true">
                    {icon}
                  </span>
                )}
                {children}
              </span>
              <div className="absolute inset-0 flex items-center justify-center">
                {loadingText ? (
                  <div className="flex items-center gap-2">
                    <Spinner size="sm" />
                    <span className="text-sm">{loadingText}</span>
                  </div>
                ) : (
                  <Spinner size="sm" />
                )}
              </div>
            </>
          ) : (
            <>
              {icon && (
                <span className="flex items-center" aria-hidden="true">
                  {icon}
                </span>
              )}
              {children}
            </>
          )}
        </button>
      );
    },
  ),
);

Button.displayName = "Button";

export const AddButton = React.memo<
  Omit<ButtonProps, "icon" | "color" | "outline" | "plain" | "variant">
>(({ children, onClick, className = "", loading, ...props }) => {
  return (
    <Button
      variant="default"
      icon={<Plus className="h-4 w-4" />}
      onClick={onClick}
      className={className}
      loading={loading}
      {...props}
    >
      {children}
    </Button>
  );
});

AddButton.displayName = "AddButton";

export const PrimaryButton = React.memo<
  Omit<ButtonProps, "color" | "outline" | "plain" | "variant">
>((props) => <Button variant="default" {...props} />);

export const SecondaryButton = React.memo<
  Omit<ButtonProps, "color" | "outline" | "plain" | "variant">
>((props) => <Button variant="outline" {...props} />);

export const TertiaryButton = React.memo<
  Omit<ButtonProps, "color" | "outline" | "plain" | "variant">
>((props) => <Button variant="ghost" {...props} />);

export const DestructiveButton = React.memo<
  Omit<ButtonProps, "color" | "outline" | "plain" | "variant">
>((props) => <Button variant="destructive" {...props} />);

PrimaryButton.displayName = "PrimaryButton";
SecondaryButton.displayName = "SecondaryButton";
TertiaryButton.displayName = "TertiaryButton";
DestructiveButton.displayName = "DestructiveButton";

type ActionButtonProps = Omit<
  ButtonProps,
  "color" | "outline" | "plain" | "variant"
> & {
  /**
   * Visual tone of the action. Defaults to the neutral `"default"` action
   * style; use `"danger"` for destructive actions (e.g. Delete) so the
   * button adopts a red hover treatment without requiring `!important`
   * overrides at the call site.
   */
  tone?: "default" | "danger";
};

export const ActionButton = React.memo<ActionButtonProps>(
  ({ children, className = "", tone = "default", ...props }) => (
    <Button
      variant={tone === "danger" ? "action-danger" : "action"}
      className={className}
      {...props}
    >
      {children}
    </Button>
  ),
);

ActionButton.displayName = "ActionButton";

export { buttonVariants };
export type { ButtonProps, ButtonColor, ButtonVariant };
